import { useStore } from 'src/contexts/StoreContext';
import { Text, TextInput, View } from 'react-native';
import { useCallback, useRef, useState } from 'react';
import { useDebounceCallback } from 'src/hooks/useDebounceCallback';
import { styles } from './ProjectTitleInput.styles';
import { observer } from 'mobx-react-lite';
import { RightSideProjectTitleInput } from './right-side-project-title-input/RightSideProjectTitleInput';
import { ColorTypography } from 'src/theme-v2/color-typography';
import { TITLE_MAX_LENGHT } from './ProjectTitleInput.constants';
import { ProjectTitleInputProps } from './ProjectTitleInput.types';

export const ProjectTitleInput = observer(({ maxWidth, focusGroup, focusId, focusOrder }: ProjectTitleInputProps) => {
  const {
    i18n,
    project: { activeProject },
  } = useStore();

  const inputRef = useRef<TextInput>(null);
  const [projectTitle, setProjectTitle] = useState<string>(activeProject?.name ?? '');
  const [width, setWidth] = useState(40);
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  const setProjectName = useDebounceCallback((text: string) => {
    activeProject?.setName(text);
  }, 1000);

  const onChangeTextHandler = useCallback(
    (text: string) => {
      if (text.length <= TITLE_MAX_LENGHT) {
        setProjectTitle(text);
        setProjectName(text);
      }
    },
    [setProjectTitle, setProjectName],
  );

  const onFocusHandler = useCallback(() => {
    setIsInputFocused(true);
  }, [setIsInputFocused]);

  const onBlurHandler = useCallback(() => {
    setIsInputFocused(false);
  }, [setIsInputFocused]);

  const onPressHandler = useCallback(async () => {
    inputRef.current?.focus();
  }, []);

  const inputFocusedStyles = isInputFocused ? [{ maxWidth }, styles.inputFocused] : [];

  return (
    <View style={[styles.input, ...inputFocusedStyles]}>
      <TextInput
        ref={inputRef}
        style={[styles.inputText, isInputFocused && styles.inputTextFocused, { width }]}
        onChangeText={onChangeTextHandler}
        value={projectTitle}
        placeholder={i18n.t('header:add-project-title')}
        placeholderTextColor={ColorTypography.TextColorPlaceholder}
        onFocus={onFocusHandler}
        onBlur={onBlurHandler}
      />

      <RightSideProjectTitleInput
        isInputFocused={isInputFocused}
        projectTitle={projectTitle}
        onPress={onPressHandler}
        focusGroup={focusGroup}
        focusId={focusId}
        focusOrder={focusOrder}
      />

      {/* Hidden text for measuring */}
      <Text
        style={styles.hiddenText}
        onLayout={e => {
          const textWidth = e.nativeEvent.layout.width || 0;
          setWidth(Math.max(40, textWidth + 14)); // add padding
        }}
      >
        {projectTitle || i18n.t('header:add-project-title')}
      </Text>
    </View>
  );
});

ProjectTitleInput.displayName = 'ProjectTitleInput';
